import { createTheme } from '@mui/material/styles';

const palettes = {
    light: {
        primaryMain: '#122743',
        background: '#F4F6F8',
        paper: '#FFFFFF',
        textPrimary: '#1A2233',
        textSecondary: '#5B6472',
        divider: 'rgba(18, 39, 67, 0.12)',
        borderSubtle: 'rgba(18, 39, 67, 0.08)',
        borderMedium: 'rgba(18, 39, 67, 0.16)',
        borderStrong: 'rgba(18, 39, 67, 0.24)',
        hoverTint: 'rgba(18, 39, 67, 0.03)',
        tableHeadBg: '#F4F6F8'
    },
    dark: {
        primaryMain: '#8893A1',
        background: '#0F1621',
        paper: '#182333',
        textPrimary: '#E7EBF2',
        textSecondary: '#98A2B3',
        divider: 'rgba(255, 255, 255, 0.12)',
        borderSubtle: 'rgba(255, 255, 255, 0.08)',
        borderMedium: 'rgba(255, 255, 255, 0.16)',
        borderStrong: 'rgba(255, 255, 255, 0.24)',
        hoverTint: 'rgba(255, 255, 255, 0.04)',
        tableHeadBg: '#1E2A3D'
    }
};

export default function getTheme(mode) {
    const p = palettes[mode === 'dark' ? 'dark' : 'light'];

    return createTheme({
        palette: {
            mode,
            primary: {
                main: p.primaryMain
            },
            secondary: {
                main: '#F6C746',
                contrastText: '#122743'
            },
            background: {
                default: p.background,
                paper: p.paper
            },
            text: {
                primary: p.textPrimary,
                secondary: p.textSecondary
            },
            divider: p.divider
        },
        shape: {
            borderRadius: 8
        },
        typography: {
            fontFamily: '"Roboto", -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif',
            h1: { fontSize: '1.75rem', fontWeight: 700, lineHeight: 1.25 },
            h2: { fontSize: '1.5rem', fontWeight: 700, lineHeight: 1.3 },
            h3: { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.35 },
            h4: { fontSize: '0.9375rem', fontWeight: 600, lineHeight: 1.4 },
            h5: { fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.4 },
            h6: { fontSize: '0.8125rem', fontWeight: 600, lineHeight: 1.4 },
            subtitle1: { fontSize: '0.9375rem', fontWeight: 500, lineHeight: 1.5 },
            subtitle2: { fontSize: '0.8125rem', fontWeight: 500, lineHeight: 1.5 },
            body1: { fontSize: '0.875rem', lineHeight: 1.5 },
            body2: { fontSize: '0.8125rem', lineHeight: 1.5 },
            caption: { fontSize: '0.75rem', lineHeight: 1.4 },
            overline: { fontSize: '0.6875rem', fontWeight: 700, letterSpacing: 1 },
            button: { fontSize: '0.8125rem', fontWeight: 600 }
        },
        components: {
            MuiButton: {
                defaultProps: {
                    size: 'small',
                    disableElevation: true
                },
                styleOverrides: {
                    root: {
                        textTransform: 'uppercase',
                        fontWeight: 600,
                        borderRadius: 8,
                        letterSpacing: 0.2,
                        padding: '6px 16px',
                        transition: 'box-shadow 0.15s ease, background-color 0.15s ease, border-color 0.15s ease',
                        '&:focus-visible': {
                            outline: '2px solid rgba(246, 199, 70, 0.5)',
                            outlineOffset: 2
                        }
                    },
                    contained: {
                        boxShadow: 'none',
                        '&:hover': {
                            boxShadow: '0 4px 12px rgba(18, 39, 67, 0.18)'
                        },
                        '&:active': {
                            boxShadow: 'none'
                        }
                    },
                    containedSecondary: {
                        '&:hover': {
                            boxShadow: '0 4px 12px rgba(246, 199, 70, 0.35)'
                        }
                    },
                    outlined: {
                        borderColor: p.borderStrong,
                        '&:hover': {
                            borderColor: p.borderStrong,
                            backgroundColor: p.hoverTint
                        }
                    },
                    text: {
                        '&:hover': {
                            backgroundColor: p.hoverTint
                        }
                    }
                }
            },
            MuiLoadingButton: {
                defaultProps: {
                    size: 'small',
                    disableElevation: true
                },
                styleOverrides: {
                    root: {
                        textTransform: 'uppercase',
                        fontWeight: 600,
                        borderRadius: 8,
                        letterSpacing: 0.2,
                        padding: '6px 16px',
                        boxShadow: 'none',
                        transition: 'box-shadow 0.15s ease, background-color 0.15s ease, border-color 0.15s ease',
                        '&:hover': {
                            boxShadow: '0 4px 12px rgba(18, 39, 67, 0.18)'
                        },
                        '&:focus-visible': {
                            outline: '2px solid rgba(246, 199, 70, 0.5)',
                            outlineOffset: 2
                        }
                    }
                }
            },
            MuiIconButton: {
                defaultProps: {
                    size: 'small'
                }
            },
            MuiTextField: {
                defaultProps: {
                    size: 'small'
                }
            },
            MuiFormControl: {
                defaultProps: {
                    size: 'small',
                    margin: 'dense'
                }
            },
            MuiSelect: {
                defaultProps: {
                    size: 'small'
                }
            },
            MuiToolbar: {
                defaultProps: {
                    variant: 'dense'
                }
            },
            MuiListItemButton: {
                defaultProps: {
                    dense: true
                }
            },
            MuiMenuItem: {
                defaultProps: {
                    dense: true
                }
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        backgroundImage: 'none'
                    },
                    elevation1: {
                        boxShadow: 'none',
                        border: `1px solid ${p.borderSubtle}`
                    },
                    elevation4: {
                        boxShadow: 'none',
                        border: `1px solid ${p.borderSubtle}`
                    }
                }
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        borderRadius: 12,
                        boxShadow: '0 1px 3px rgba(18, 39, 67, 0.08), 0 1px 2px rgba(18, 39, 67, 0.06)'
                    }
                }
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        borderRadius: 8,
                        backgroundColor: p.paper,
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: p.borderMedium
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: p.borderStrong
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#F6C746',
                            borderWidth: 1.5
                        }
                    }
                }
            },
            MuiInputLabel: {
                styleOverrides: {
                    root: {
                        '&.Mui-focused': {
                            color: '#F6C746'
                        }
                    }
                }
            },
            MuiChip: {
                defaultProps: {
                    size: 'small'
                },
                styleOverrides: {
                    root: {
                        borderRadius: 8,
                        fontWeight: 500
                    }
                }
            },
            MuiDialog: {
                styleOverrides: {
                    paper: {
                        borderRadius: 12
                    }
                }
            },
            MuiDialogTitle: {
                styleOverrides: {
                    root: {
                        fontSize: '1.375rem',
                        fontWeight: 600,
                        lineHeight: 1.3,
                        padding: '14px 20px'
                    }
                }
            },
            MuiDialogContent: {
                styleOverrides: {
                    root: {
                        padding: '14px 20px 12px'
                    }
                }
            },
            MuiDialogActions: {
                styleOverrides: {
                    root: {
                        padding: '10px 20px'
                    }
                }
            },
            MuiTableCell: {
                styleOverrides: {
                    root: {
                        padding: '8px 12px',
                        borderBottom: `1px solid ${p.borderSubtle}`
                    },
                    head: {
                        fontWeight: 700,
                        fontSize: 11,
                        letterSpacing: 0.4,
                        textTransform: 'uppercase',
                        color: p.textSecondary,
                        backgroundColor: p.tableHeadBg,
                        borderBottom: `1px solid ${p.divider}`
                    }
                }
            },
            MuiTableRow: {
                styleOverrides: {
                    root: {
                        '&:hover:not(.MuiTableRow-head)': {
                            backgroundColor: p.hoverTint
                        },
                        '&.Mui-selected': {
                            backgroundColor: 'rgba(246, 199, 70, 0.12)'
                        },
                        '&.Mui-selected:hover': {
                            backgroundColor: 'rgba(246, 199, 70, 0.2)'
                        }
                    }
                }
            },
            MuiTableSortLabel: {
                styleOverrides: {
                    root: {
                        '&.Mui-active': {
                            color: '#F6C746'
                        },
                        '&:hover': {
                            color: '#F6C746'
                        }
                    },
                    icon: {
                        color: 'inherit !important'
                    }
                }
            },
            MuiTablePagination: {
                styleOverrides: {
                    root: {
                        fontSize: 13
                    },
                    toolbar: {
                        minHeight: 44,
                        paddingLeft: 8
                    },
                    selectLabel: {
                        marginBottom: 0
                    },
                    displayedRows: {
                        marginBottom: 0
                    }
                }
            },
            MuiTooltip: {
                styleOverrides: {
                    tooltip: {
                        borderRadius: 6,
                        fontSize: 12
                    }
                }
            },
            MuiDataGrid: {
                defaultProps: {
                    density: 'compact'
                },
                styleOverrides: {
                    root: {
                        border: 'none',
                        '& .MuiDataGrid-cell': {
                            borderBottom: `1px solid ${p.borderSubtle}`
                        },
                        '& .MuiDataGrid-row:hover': {
                            backgroundColor: p.hoverTint
                        },
                        '& .MuiDataGrid-row.Mui-selected': {
                            backgroundColor: 'rgba(246, 199, 70, 0.12)'
                        },
                        '& .MuiDataGrid-row.Mui-selected:hover': {
                            backgroundColor: 'rgba(246, 199, 70, 0.2)'
                        }
                    },
                    columnHeaders: {
                        backgroundColor: p.tableHeadBg,
                        fontWeight: 700,
                        fontSize: 11,
                        letterSpacing: 0.4,
                        textTransform: 'uppercase',
                        color: p.textSecondary,
                        borderBottom: `1px solid ${p.divider}`
                    }
                }
            }
        },
        overrides: {
            MUIDataTableHeadRow: {
                root: {
                    backgroundColor: p.tableHeadBg
                }
            },
            MUIDataTableBodyCell: {
                root: {
                    maxWidth: '20vw'
                }
            }
        }
    });
}
